import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Gift, Download } from "lucide-react";

export default function RedemptionsPage() {
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [codes, setCodes] = useState<any[]>([]);
  const [codesLoading, setCodesLoading] = useState(false);

  useEffect(() => {
    api.get("/api/v1/redemptions/groups").then(setGroups).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  const handleCreateGroup = async () => {
    const name = prompt("Group name:");
    if (!name) return;
    try {
      const result = await api.post("/api/v1/redemptions/groups", { name });
      setGroups([{ id: result.id, name }, ...groups]);
      toast.success("Group created");
    } catch (e: any) { toast.error(e.message); }
  };

  const handleDeleteGroup = async (id: number) => {
    if (!confirm("Delete this group and all its codes?")) return;
    try { await api.delete(`/api/v1/redemptions/groups/${id}`); setGroups(groups.filter(g => g.id !== id)); if (selectedGroup === id) { setSelectedGroup(null); setCodes([]); } toast.success("Deleted"); }
    catch (e: any) { toast.error(e.message); }
  };

  const loadCodes = async (groupId: number) => {
    setSelectedGroup(groupId);
    setCodesLoading(true);
    try { const data = await api.get(`/api/v1/redemptions/groups/${groupId}/codes`); setCodes(data); }
    catch (e: any) { toast.error(e.message); }
    finally { setCodesLoading(false); }
  };

  const handleGenerate = async () => {
    if (!selectedGroup) return;
    const countStr = prompt("How many codes to generate?", "10");
    if (!countStr) return;
    try {
      await api.post(`/api/v1/redemptions/groups/${selectedGroup}/codes/generate`, { count: parseInt(countStr) });
      loadCodes(selectedGroup);
      toast.success(`Generated ${countStr} codes`);
    } catch (e: any) { toast.error(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Redemptions</h1>
        <button onClick={handleCreateGroup} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Group
        </button>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Groups</h2>
          {loading ? <div className="animate-pulse space-y-2">{[1,2].map(i=><div key={i} className="h-12 bg-muted rounded-lg"/>)}</div> : groups.map(g => (
            <div key={g.id} onClick={() => loadCodes(g.id)} className={`bg-card rounded-xl border p-4 flex items-center gap-3 cursor-pointer transition-colors ${selectedGroup === g.id ? "border-primary" : "border-border hover:border-primary/30"}`}>
              <Gift className="w-5 h-5 text-primary" />
              <span className="flex-1 font-medium text-sm">{g.name}</span>
              <button onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g.id); }} className="p-1 rounded hover:bg-destructive/10"><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></button>
            </div>
          ))}
        </div>
        <div className="lg:col-span-2">
          {selectedGroup ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Codes</h2>
                <button onClick={handleGenerate} className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Generate
                </button>
              </div>
              {codesLoading ? <div className="animate-pulse space-y-2">{[1,2,3].map(i=><div key={i} className="h-10 bg-muted rounded-lg"/>)}</div> : codes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No codes yet. Click Generate to create some.</div>
              ) : (
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {codes.map(c => (
                    <div key={c.id} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-card border border-border text-sm">
                      <code className="font-mono text-xs flex-1">{c.code}</code>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === "available" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{c.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground text-sm">Select a group to view codes.</div>
          )}
        </div>
      </div>
    </div>
  );
}
